import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, FileText, Clock, Globe, CheckCircle, 
  AlertCircle, ArrowRight, MapPin,
  Languages, Banknote, Timer, Sun, Shield, Plane, CalendarDays, Bookmark,
  Share2, Copy, Check, X
} from 'lucide-react';
import { countriesApi } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import CountryFlag from '../components/CountryFlag';
import { toast } from 'sonner';

const VisaRequirementsPage = () => {
  const { countrySlug, countryId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shareOpen, setShareOpen] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let response;
        if (countrySlug) {
          response = await countriesApi.getBySlug(countrySlug);
        } else if (countryId) {
          response = await countriesApi.getRequirements(countryId);
        }
        setData(response.data);
      } catch (error) {
        console.error('Error fetching requirements:', error);
        toast.error('Failed to load visa requirements');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [countrySlug, countryId]);

  const getShareUrl = () => {
    const slug = data?.country?.code?.toLowerCase();
    return `${window.location.origin}/countries/${slug}`;
  };

  const getShareText = (countryName) => {
    return `Check out visa requirements for ${countryName} on EasyMyVisa! Apply online with fast processing.`;
  };

  const handleShare = async () => {
    if (!data) return;
    const url = getShareUrl();
    const text = getShareText(data.country.name);

    // Try native share on mobile
    if (navigator.share) {
      try {
        await navigator.share({ title: `${data.country.name} Visa Requirements - EasyMyVisa`, text, url });
        return;
      } catch (err) {
        if (err.name === 'AbortError') return;
      }
    }
    setShareOpen(true);
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(getShareUrl());
      setCopied(true);
      toast.success('Link copied to clipboard!');
      setTimeout(() => { setCopied(false); setShareOpen(false); }, 1500);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const handleWhatsApp = () => {
    const url = getShareUrl();
    const text = getShareText(data.country.name);
    window.open(`https://wa.me/?text=${encodeURIComponent(text + '\n' + url)}`, '_blank');
    setShareOpen(false);
  };

  const handleEmail = () => {
    const url = getShareUrl();
    const subject = `${data.country.name} Visa Requirements - EasyMyVisa`;
    const body = getShareText(data.country.name) + '\n\n' + url;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
    setShareOpen(false);
  };

  const handleTwitter = () => {
    const url = getShareUrl();
    const text = getShareText(data.country.name);
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`, '_blank');
    setShareOpen(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <Navbar />
        <main className="pt-0">
          <div className="h-[380px] bg-gray-200 animate-pulse" />
          <div className="max-w-6xl mx-auto px-4 -mt-16">
            <div className="animate-pulse space-y-6">
              <div className="h-24 bg-white rounded-xl" />
              <div className="h-64 bg-white rounded-xl" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F5F0E6]">
        <Navbar />
        <main className="pt-24 pb-16 px-4 text-center">
          <Globe className="w-16 h-16 text-[#D1CBBF] mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-[#0A1E40] mb-2">Country not found</h2>
          <Button onClick={() => navigate('/countries')}>Browse Countries</Button>
        </main>
      </div>
    );
  }

  const { country, visa_types } = data;
  const slug = country.code?.toLowerCase();

  const infoItems = [
    { icon: <MapPin className="w-4 h-4" />, label: 'Capital', value: country.capital },
    { icon: <Banknote className="w-4 h-4" />, label: 'Currency', value: country.currency },
    { icon: <Languages className="w-4 h-4" />, label: 'Language', value: country.language },
    { icon: <Timer className="w-4 h-4" />, label: 'Timezone', value: country.timezone_info },
    { icon: <Sun className="w-4 h-4" />, label: 'Best Time', value: country.best_time_to_visit },
  ].filter(item => item.value);

  return (
    <div className="min-h-screen bg-[#F5F0E6]">
      <SEO
        title={country.seo_title || `${country.name} Visa - Requirements, Types & Apply Online`}
        description={country.seo_description || `Apply for ${country.name} visa online. Check visa types, requirements, processing times, and fees. ${country.description || ''}`}
        keywords={country.seo_keywords || `${country.name} visa, ${country.name} visa requirements, ${country.name} visa fee, ${country.name} tourist visa, ${country.name} business visa, apply ${country.name} visa online`}
        canonical={`/countries/${slug}`}
        ogImage={country.og_image || country.image_url}
        ogTitle={country.og_title}
        ogDescription={country.og_description}
        jsonLd={{
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "Country",
              "name": country.name,
              "alternateName": country.code,
              "image": country.image_url,
              "description": country.description
            },
            {
              "@type": "WebPage",
              "name": `${country.name} Visa - Requirements & Apply Online`,
              "description": `Apply for ${country.name} visa online. Check visa types, requirements, processing times, and fees.`,
              "url": `${window.location.origin}/countries/${slug}`,
              "breadcrumb": {
                "@type": "BreadcrumbList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
                  { "@type": "ListItem", "position": 2, "name": "Countries", "item": `${window.location.origin}/countries` },
                  { "@type": "ListItem", "position": 3, "name": country.name, "item": `${window.location.origin}/countries/${slug}` }
                ]
              }
            },
            ...(visa_types.length > 0 ? [{
              "@type": "ItemList",
              "name": `${country.name} Visa Types`,
              "itemListElement": visa_types.map((v, i) => ({
                "@type": "ListItem",
                "position": i + 1,
                "name": v.name,
                "description": `Processing: ${v.processing_time}, Validity: ${v.validity}, Fee: ${v.fee}`
              }))
            }] : [])
          ]
        }}
      />
      <Navbar />
      
      {/* Full-width Banner */}
      <div className="relative h-[380px] overflow-hidden" data-testid="country-banner">
        <img 
          src={country.image_url} 
          alt={`${country.name} Visa`}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A1E40] via-[#0A1E40]/40 to-transparent" />
        
        <div className="absolute top-20 left-0 right-0 px-4">
          <div className="max-w-6xl mx-auto">
            <Button 
              variant="ghost" 
              onClick={() => navigate(-1)} 
              className="text-white/80 hover:text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
          </div>
        </div>

        <div className="absolute bottom-0 left-0 right-0 px-4 pb-10">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-end justify-between gap-4">
              <div className="flex items-end gap-4">
                <span className="text-6xl drop-shadow-lg"><CountryFlag code={country.code} size="2xl" /></span>
                <div>
                  <h1 className="text-4xl md:text-5xl font-bold text-white mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {country.name}
                  </h1>
                  <p className="text-white/70 text-base md:text-lg max-w-2xl">{country.description}</p>
                </div>
              </div>
              <div className="relative shrink-0">
                <Button
                  variant="ghost"
                  onClick={handleShare}
                  className="bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl px-4 py-2.5"
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" /> Share
                </Button>
                
                {/* Share Dropdown */}
                {shareOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setShareOpen(false)} />
                    <div className="absolute right-0 bottom-full mb-2 w-56 bg-white rounded-xl shadow-xl border border-[#D1CBBF] z-50 overflow-hidden" data-testid="share-dropdown">
                      <div className="px-4 py-3 bg-[#F5F0E6] border-b border-[#E8E2D6] flex items-center justify-between">
                        <span className="text-sm font-semibold text-[#0A1E40]">Share this page</span>
                        <button onClick={() => setShareOpen(false)} className="text-[#4A5568] hover:text-[#0A1E40]">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <div className="p-2">
                        <button
                          onClick={handleWhatsApp}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F5F0E6] transition-colors text-left"
                          data-testid="share-whatsapp"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#25D366] flex items-center justify-center shrink-0">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                          </div>
                          <span className="text-sm font-medium text-[#0A1E40]">WhatsApp</span>
                        </button>
                        <button
                          onClick={handleTwitter}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F5F0E6] transition-colors text-left"
                          data-testid="share-twitter"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#0A1E40] flex items-center justify-center shrink-0">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
                          </div>
                          <span className="text-sm font-medium text-[#0A1E40]">X (Twitter)</span>
                        </button>
                        <button
                          onClick={handleEmail}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F5F0E6] transition-colors text-left"
                          data-testid="share-email"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#FF5A36] flex items-center justify-center shrink-0">
                            <svg className="w-4 h-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/></svg>
                          </div>
                          <span className="text-sm font-medium text-[#0A1E40]">Email</span>
                        </button>
                        <button
                          onClick={handleCopyLink}
                          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-[#F5F0E6] transition-colors text-left"
                          data-testid="share-copy-link"
                        >
                          <div className="w-8 h-8 rounded-full bg-[#4A5568] flex items-center justify-center shrink-0">
                            {copied ? <Check className="w-4 h-4 text-white" /> : <Copy className="w-4 h-4 text-white" />}
                          </div>
                          <span className="text-sm font-medium text-[#0A1E40]">{copied ? 'Copied!' : 'Copy Link'}</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="pb-16 px-4" data-testid="visa-requirements-page">
        <div className="max-w-6xl mx-auto">

          {/* Country Info Bar */}
          {infoItems.length > 0 && (
            <div className="bg-white rounded-2xl border border-[#D1CBBF] shadow-sm -mt-8 relative z-10 mb-8" data-testid="country-info-bar">
              <div className={`grid grid-cols-2 md:grid-cols-${Math.min(infoItems.length, 5)} divide-y md:divide-y-0 divide-x-0 md:divide-x divide-[#E8E2D6]`}>
                {infoItems.map((item, idx) => (
                  <div key={idx} className={`p-4 md:p-5 text-center ${idx === infoItems.length - 1 && infoItems.length % 2 !== 0 ? 'col-span-2 md:col-span-1' : ''}`} data-testid={`info-${item.label.toLowerCase().replace(' ','-')}`}>
                    <div className="flex items-center justify-center gap-1.5 mb-1.5">
                      <span className="text-[#FF5A36]">{item.icon}</span>
                      <span className="text-[11px] font-semibold uppercase tracking-wider text-[#9CA3AF]">{item.label}</span>
                    </div>
                    <p className="text-sm font-semibold text-[#0A1E40]">{item.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column: Visa Types */}
            <div className="lg:col-span-2 space-y-6">
              <div data-testid="visa-types-section">
                <h2 className="text-xl font-bold text-[#0A1E40] mb-5 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  <Globe className="w-5 h-5 text-[#FF5A36]" />
                  Available Visa Types
                </h2>

                {visa_types.length === 0 ? (
                  <div className="bg-white rounded-xl p-8 border border-[#D1CBBF] text-center">
                    <AlertCircle className="w-12 h-12 text-[#D1CBBF] mx-auto mb-3" />
                    <p className="text-[#4A5568]">No visa types available yet.</p>
                  </div>
                ) : (
                  <div className="space-y-5">
                    {visa_types.map((visa) => (
                      <div 
                        key={visa.visa_type_id} 
                        className="bg-white rounded-2xl border border-[#D1CBBF] overflow-hidden hover:shadow-lg transition-all duration-300"
                        data-testid={`visa-type-${visa.visa_type_id}`}
                      >
                        {/* Top row: name + price */}
                        <div className="px-6 pt-6 pb-4">
                          <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Plane className="w-4 h-4 text-[#FF5A36]" />
                                <h3 className="text-lg font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                  {visa.name}
                                </h3>
                              </div>
                              {visa.description && (
                                <p className="text-sm text-[#4A5568] mt-1">{visa.description}</p>
                              )}
                            </div>
                            <div className="text-right shrink-0">
                              <p className="text-2xl font-bold text-[#0A1E40]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                {visa.fee?.toLocaleString('en-IN', { style: 'currency', currency: visa.currency || 'INR', maximumFractionDigits: 0 })}
                              </p>
                              <p className="text-xs text-[#9CA3AF]">per person</p>
                            </div>
                          </div>
                        </div>

                        {/* Info chips */}
                        <div className="px-6 pb-4">
                          <div className="flex flex-wrap gap-3">
                            <div className="flex items-center gap-1.5 bg-[#F5F0E6] rounded-lg px-3 py-2">
                              <Clock className="w-3.5 h-3.5 text-[#FF5A36]" />
                              <div>
                                <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold tracking-wide">Processing</p>
                                <p className="text-xs font-semibold text-[#0A1E40]">{visa.processing_time}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 bg-[#F5F0E6] rounded-lg px-3 py-2">
                              <Shield className="w-3.5 h-3.5 text-[#FF5A36]" />
                              <div>
                                <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold tracking-wide">Validity</p>
                                <p className="text-xs font-semibold text-[#0A1E40]">{visa.validity}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 bg-[#F5F0E6] rounded-lg px-3 py-2">
                              <CalendarDays className="w-3.5 h-3.5 text-[#FF5A36]" />
                              <div>
                                <p className="text-[10px] text-[#9CA3AF] uppercase font-semibold tracking-wide">Max Stay</p>
                                <p className="text-xs font-semibold text-[#0A1E40]">{visa.max_stay}</p>
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Eligibility + Documents */}
                        <div className="px-6 pb-4 space-y-3">
                          {visa.requirements?.length > 0 && (
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-[#4A5568] mb-2 flex items-center gap-1">
                                <Bookmark className="w-3 h-3" /> Eligibility
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {visa.requirements.map((req, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-emerald-50 border border-emerald-100 rounded-md text-xs text-emerald-700">
                                    <CheckCircle className="w-3 h-3" /> {req}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}

                          {visa.documents_required?.length > 0 && (
                            <div>
                              <p className="text-xs font-bold uppercase tracking-wider text-[#4A5568] mb-2 flex items-center gap-1">
                                <FileText className="w-3 h-3" /> Documents Required
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {visa.documents_required.map((doc, i) => (
                                  <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 bg-blue-50 border border-blue-100 rounded-md text-xs text-blue-700">
                                    <FileText className="w-3 h-3" /> {doc}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Apply button */}
                        <div className="px-6 pb-6 pt-1">
                          <Link
                            to={user ? `/apply?country=${country.country_id}&visa=${visa.visa_type_id}` : '/login'}
                            state={!user ? { from: `/apply?country=${country.country_id}&visa=${visa.visa_type_id}` } : undefined}
                          >
                            <Button className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white h-12 font-semibold rounded-xl text-sm" data-testid={`apply-btn-${visa.visa_type_id}`}>
                              Apply for {visa.name} <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Right Column: Requirements, Notes, CTA */}
            <div className="space-y-6">
              {/* General Requirements */}
              {country.requirements?.length > 0 && (
                <div className="bg-white rounded-2xl p-6 border border-[#D1CBBF]" data-testid="requirements-section">
                  <h2 className="text-lg font-bold text-[#0A1E40] mb-4 flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    General Requirements
                  </h2>
                  <div className="space-y-2">
                    {country.requirements.map((req, index) => (
                      <div key={index} className="flex items-start gap-3 p-3 rounded-xl bg-[#F5F0E6]/60">
                        <div className="w-6 h-6 bg-[#FF5A36]/10 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                          <span className="text-[#FF5A36] text-xs font-bold">{index + 1}</span>
                        </div>
                        <span className="text-sm text-[#0A1E40] leading-relaxed">{req}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Important Notes */}
              {(country.important_notes?.length > 0 || true) && (
                <div className="rounded-2xl overflow-hidden border border-amber-200" data-testid="important-notes-section">
                  <div className="bg-amber-500 px-5 py-3">
                    <h3 className="font-bold text-white flex items-center gap-2 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      <AlertCircle className="w-4 h-4" /> Important Notes
                    </h3>
                  </div>
                  <div className="bg-amber-50 px-5 py-4">
                    <ul className="space-y-3">
                      {(country.important_notes?.length > 0 ? country.important_notes : [
                        'Passport must be valid for at least 6 months from travel date',
                        'Processing times are estimates and may vary',
                        'All documents must be in English or with certified translations',
                        'Fees are non-refundable once application is submitted'
                      ]).map((note, i) => (
                        <li key={i} className="flex items-start gap-3 text-sm text-amber-900">
                          <div className="w-5 h-5 bg-amber-200 rounded-full flex items-center justify-center shrink-0 mt-0.5">
                            <span className="text-amber-700 text-[10px] font-bold">{i + 1}</span>
                          </div>
                          <span className="leading-relaxed">{note}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}

              {/* Need Help CTA */}
              <div className="bg-[#0A1E40] rounded-2xl p-6 text-center">
                <Shield className="w-8 h-8 text-[#FF5A36] mx-auto mb-3" />
                <h3 className="text-white font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Need Help?</h3>
                <p className="text-white/60 text-sm mb-4">Our visa experts are here to guide you through the process.</p>
                <Link to="/contact">
                  <Button className="w-full bg-[#FF5A36] hover:bg-[#E0492B] text-white rounded-xl" data-testid="contact-help-btn">
                    Contact Us
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default VisaRequirementsPage;
